// The look (LOOKS.md): what the site looks like — type, shape, line, elevation,
// motion, icons and its ground — chosen per person, the same shape as the
// detail level. Pure and sport-agnostic.
//
// Three axes that multiply and never merge (LOOKS.md Part 2): the look is how
// a thing is drawn, the detail level is how many numbers ride on it, the screen
// size is how much fits. So a look never sets density; the detail level owns
// row height and panel padding.
//
// WHAT LIVES HERE IS THE CONTRACT, NOT THE LOOKS. Each look is a folder in the
// web app (apps/web/src/looks/<id>/: its definition and its stylesheet), found
// by the app's registry, so a new look is dropped in rather than wired up
// (LOOKS.md Part 9). This file says what every look must supply: the tokens its
// stylesheet defines, the shape of its ground, and the definition itself.
//
// A look is an id, not a bundle. The stored value is `'clean'`, never a pile of
// resolved values, so a look can be improved without touching anybody's
// storage and a retired one falls back to the default.

/** A look's id: its folder's name. */
export type LookId = string;

/** Clean: built to stand beside Sleeper and ESPN on a first visit (LOOKS.md, 2026-09-24). */
export const DEFAULT_LOOK: LookId = 'clean';

/**
 * THE GROUND: the neutral layers a look may set in place of the palette's —
 * the surfaces and the ink on them. The palette still owns the accent and a
 * team's colors, which sit on top of whatever ground the look lays.
 */
export const GROUND_TOKENS = [
  'bg-canvas',
  'bg-surface',
  'bg-raised',
  'bg-sunken',
  'border-subtle',
  'border-strong',
  'ink-primary',
  'ink-secondary',
  'ink-muted',
] as const;
export type GroundToken = (typeof GROUND_TOKENS)[number];

/**
 * THE MARKS: the colors read as words — a result, a live game, a position. A
 * look may give its own (Crayon's crayon box on paper, its chalk pastels on the
 * board); whatever it gives, on its own ground each is lifted until it reads.
 */
export const MARK_TOKENS = [
  'win',
  'loss',
  'warn',
  'live',
  'info',
  'pos-qb',
  'pos-rb',
  'pos-wr',
  'pos-te',
  'pos-k',
  'pos-dst',
  'pos-dl',
  'pos-lb',
  'pos-db',
] as const;
export type MarkToken = (typeof MARK_TOKENS)[number];

/** One mode's ground: every neutral layer, and any marks the look draws in its own colors. */
export type LookGround = Record<GroundToken, string> & Partial<Record<MarkToken, string>>;

/** A look's ground in each mode, or null to sit on the palette's (Clean). */
export type LookGrounds = { dark: LookGround; light: LookGround } | null;

/**
 * What a look folder's definition says about itself. The web app's own
 * definition extends this with its drawings (icons, award glyphs, art, the
 * filters its lines are drawn through).
 */
export interface LookBasics {
  /** The folder's name, and the stored value. Lowercase letters and dashes. */
  id: LookId;
  /** What a person sees it called. */
  label: string;
  /** It in a line, for the picker: what it is like, never which is better. */
  blurb: string;
  /** Where it sits in the picker; Clean is 0. */
  order: number;
  grounds: LookGrounds;
  /**
   * The web fonts it draws with beyond the page's own, loaded only when used
   * or previewed: a loader that brings in the site's own copies, since a page
   * asks no other company for anything it needs (DIRECTION D12).
   */
  fonts: (() => Promise<unknown>) | null;
  /**
   * A look drawn in one mode only — Scoreboard is a board at night — says so,
   * and the person's own Mode waits until they choose another look. Absent
   * is both.
   */
  mode?: 'dark' | 'light';
}

/**
 * THE LOOK CONTRACT: every custom property a look must define, and the only
 * ones a look may define. A screen or kit stylesheet reads these and never a
 * raw font, radius, line width, shadow or duration, which is what makes a
 * second look a stylesheet rather than a fork.
 *
 * Colors are not here: they are the palette's (`resolveTheme`) and the look's
 * ground. Spacing is not here either: density belongs to the detail level.
 *
 * The web app's look tests read each look's stylesheet against this list.
 */
export const LOOK_TOKENS = [
  // Type
  '--fsp-font-body',
  '--fsp-font-display',
  '--fsp-font-mono',
  '--fsp-weight-regular',
  '--fsp-weight-medium',
  '--fsp-weight-strong',
  '--fsp-weight-bold',
  '--fsp-weight-display',
  '--fsp-numerals',
  '--fsp-tracking-caps',
  // Every look's text the same apparent size (`font-size-adjust`): a hand
  // font's small letters would otherwise make one look's Large another's
  // Default. Clean's is its own face's, which is none.
  '--fsp-type-adjust',
  // Shape
  '--fsp-radius-xs',
  '--fsp-radius-sm',
  '--fsp-radius-md',
  '--fsp-radius-lg',
  '--fsp-radius-pill',
  // The same corners as one length each, for anything that does arithmetic on
  // a radius — the component library does (`calc(var(--input-radius) - 1px)`),
  // and a hand-drawn eight-value radius is not a length
  '--fsp-radius-plain-xs',
  '--fsp-radius-plain-sm',
  '--fsp-radius-plain-lg',
  // Line: what a heavier line is allowed to mean is the look's call too
  '--fsp-line-width',
  '--fsp-line-strong-width',
  '--fsp-line-accent-width',
  '--fsp-line-style',
  // Elevation
  '--fsp-shadow-raised',
  '--fsp-shadow-overlay',
  // Motion
  '--fsp-motion-fast',
  '--fsp-motion-base',
  '--fsp-motion-slow',
  '--fsp-ease-standard',
  '--fsp-ease-emphasis',
  // Icons: the set is a registry in the kit; the stroke is a token, and so is
  // the filter a look draws every line of an icon through (Crayon's wobble)
  '--fsp-icon-stroke',
  '--fsp-icon-filter',
  // The ground's grain: an image laid over the canvas (Crayon's paper fiber
  // and chalk dust), none in Clean
  '--fsp-ground-texture',
] as const;

export type LookToken = (typeof LOOK_TOKENS)[number];
